import React from 'react';
import { useQueryClient } from 'react-query';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Popconfirm, Tooltip } from 'antd';
import cn from 'classnames';
import { AiFillFileExclamation } from 'react-icons/ai';
import useQuestionsQueryParams from '../../hooks/useQuestionsQueryParams';
import { questionsQueryKeys } from '../../queries/questionsQueryKeys';
import { setSelectedQuestion } from '../../store/actions';
import useQuestionImportantMutation from '../../mutations/useQuestionImportantMutation';
import getName from '../../../../helpers/getName';
import styles from './ImportantIndicatorToggle.module.scss';

const updateQuestion = ({ question, isImportant, userInfo, userInfoKeys }) => {
  if (isImportant) {
    const setImportantWorker = userInfoKeys.reduce((acc, key) => {
      if (userInfo[key]) {
        const newKey =
          key === 'WorkerId' ? 'id' : `${key[0].toLowerCase()}${key.slice(1)}`;
        acc[newKey] =
          key === 'WorkerId' ? Number(userInfo[key]) : userInfo[key];
      }
      return acc;
    }, {});

    return {
      ...question,
      isImportant,
      setImportantWorker,
    };
  } else {
    const { setImportantWorker, ...restQuestion } = question;

    return { ...restQuestion, isImportant };
  }
};

const Indicator = ({ hideInactiveIndicator = true, isActive, ...props }) => {
  if (hideInactiveIndicator && !isActive) {
    return null;
  }

  return (
    <AiFillFileExclamation
      {...props}
      className={cn(styles.indicator, {
        [styles.activeIndicator]: isActive,
      })}
    />
  );
};

const Toggle = ({ question, className }) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const questionsQueryParams = useQuestionsQueryParams();

  const userInfo = useSelector((state) => state.authentication.userInfo);

  const { isImportant, id: questionId } = question;

  const handleChangeImportantSuccess = (data) => {
    if (data !== 'Error') {
      const userInfoKeys = ['SurName', 'FirstName', 'PatrName', 'WorkerId'];
      const questionsKey = questionsQueryKeys.questions(questionsQueryParams);

      const baseParams = {
        isImportant: !isImportant,
        userInfo,
        userInfoKeys,
      };

      const updatedSelectedQuestion = updateQuestion({
        question,
        ...baseParams,
      });

      const oldQuestions = queryClient.getQueryData(questionsKey);

      const updatedQuestions = oldQuestions.map((oldQuestion) => {
        if (oldQuestion.id === questionId) {
          const updatedQuestion = updateQuestion({
            question: oldQuestion,
            ...baseParams,
          });

          return updatedQuestion;
        }

        return oldQuestion;
      });

      queryClient.cancelQueries(questionsKey);
      queryClient.setQueryData(questionsKey, updatedQuestions);

      dispatch(setSelectedQuestion(updatedSelectedQuestion));
    }
  };

  const { mutate: changeImportant, isLoading: isChangeImportantLoading } =
    useQuestionImportantMutation({ onSuccess: handleChangeImportantSuccess });

  const handleConfirm = () => {
    changeImportant({ questionId, important: !isImportant });
  };

  const title = isImportant
    ? 'Снять важность с вопроса'
    : 'Пометить вопрос как важный';

  return (
    <Tooltip title={title} placement="left">
      <Popconfirm
        title={`${title}?`}
        onConfirm={handleConfirm}
        okText="Да"
        cancelText="Нет"
        placement="bottom"
        disabled={isChangeImportantLoading}
      >
        <Button
          loading={isChangeImportantLoading}
          className={cn(styles.toggleBtn, className)}
        >
          {!isChangeImportantLoading && (
            <Indicator hideInactiveIndicator={false} isActive={isImportant} />
          )}
        </Button>
      </Popconfirm>
    </Tooltip>
  );
};

const ImportantIndicatorToggle = ({
  isToggle = false,
  hideInactiveIndicator,
  question,
  toggleClass,
}) => {
  const { WorkerId } = useSelector((state) => state.authentication.userInfo);

  const { isImportant, setImportantWorker } = question || {};

  const creatorName =
    WorkerId && Number(WorkerId) === setImportantWorker?.id
      ? 'Я'
      : getName(setImportantWorker);

  return isToggle ? (
    <Toggle question={question} className={toggleClass} />
  ) : (
    <Tooltip title={creatorName ? `Отметил: ${creatorName}` : ''}>
      <Indicator
        hideInactiveIndicator={hideInactiveIndicator}
        isActive={isImportant}
      />
    </Tooltip>
  );
};

export default ImportantIndicatorToggle;
